/**
 * alp-cart.js — shared add-to-cart helper for the Alpine PDP.
 *
 * Single source for the "POST /cart/add.js → refresh mini-cart → open drawer"
 * flow that add-to-cart.liquid, make-it-a-match.liquid and quick-add-modal.liquid
 * each used to copy. Keeping it here means the three stay in sync.
 *
 * Locale-safe: reads window.routes.cart_add_url (set in layout/theme.liquid) so
 * it POSTs to the right localized route; falls back to '/cart/add'.
 *
 * Usage (returns a promise; THROWS on HTTP failure so callers can show an error):
 *   await window.alpAddToCart({ id: variantId, quantity: 1 });
 *   await window.alpAddToCart({ id }, { openDrawer: false }); // add without opening
 *
 * Loaded with `defer` like the other alp-*.js assets. Framework-agnostic — it
 * touches no Alpine state, so it works from any block, store or vanilla handler.
 */
(function () {
  'use strict';
  if (window.alpAddToCart) return;

  // ---- Analytics -----------------------------------------------------------
  // NOTE: Analytics events for addToCart are now handled by global.js, which
  // monitors all fetch calls to /cart/add.js via a fetch wrapper (line 2420+)
  // and pushes the 'addToCart' dataLayer event via pushAddDeltaIfAny().
  // This ensures a single event is fired regardless of the add method (Alpine or legacy).
  // --------------------------------------------------------------------------

  // Legacy "side cart viewed" push — fired once when the drawer opens.
  function pushViewedSideCartDL() {
    try {
      if (typeof window.getEnrichedCart === 'function') window.getEnrichedCart();
      (window.dataLayer = window.dataLayer || []).push({ event: 'viewedSideCart', _clear: true });
    } catch (e) {
      console.warn('[alp-cart] viewedSideCart dataLayer push failed', e);
    }
  }
  // --------------------------------------------------------------------------

  /**
   * Pre-order mixed cart guard, add side. A pre-order add is always blocked when the
   * cart holds normal lines; the reverse (normal add into a pre-order cart) is only
   * blocked when the caller opts in (PDP), so quick add stays usable and is caught
   * by the checkout guard instead.
   * Gated by window.__preorderMixedCartGuardEnabled (settings.enable_preorder_mixed_cart_guard).
   */
  async function checkPreorderGuard(candidateIsPreorder, blockNormalAdd) {
    if (!window.__preorderMixedCartGuardEnabled) return;
    if (!candidateIsPreorder && !blockNormalAdd) return;
    var cartUrl = ((window.routes && window.routes.cart_url) || '/cart') + '.js';
    var cart = await fetch(cartUrl, { credentials: 'same-origin', cache: 'no-store' })
      .then(function (response) {
        return response.ok ? response.json() : Promise.reject(new Error('cart fetch failed'));
      })
      .catch(function () { return null; });
    if (!cart) return;
    var items = Array.isArray(cart.items) ? cart.items : [];
    var isPreorderItem = function (line) {
      return !!(
        line &&
        (line.selling_plan_allocation ||
          (line.properties && line.properties['Pre-ordered items'] != null))
      );
    };
    var blocked = candidateIsPreorder
      ? items.some(function (line) { return line && !isPreorderItem(line); })
      : items.some(isPreorderItem);
    if (!blocked) return;
    var err = new Error(
      (candidateIsPreorder ? window.__preorderPdpMessage : window.__preorderCartMessage) ||
        'Pre-order items cannot be combined with other products in the same order.'
    );
    err.preorderBlocked = true;
    throw err;
  }

  async function alpAddToCart(item, opts) {
    opts = opts || {};

    await checkPreorderGuard(!!opts.preorder, !!opts.blockNormalAdd);
    
    var addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';

    var res = await fetch(addUrl + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(item),
    });
    if (!res.ok) throw new Error('cart/add ' + res.status);
    var data = await res.json();

    // One refresh is enough — updateminicart/updatecart do the same
    // header-minicart fetch+replace; calling both doubled the work.
    if (window.updateminicart) await window.updateminicart();
    else if (window.updatecart) await window.updatecart();

    // Analytics: global.js already monitors fetch calls to /cart/add.js and pushes
    // the addToCart dataLayer event via pushAddDeltaIfAny(), so we don't push here
    // to avoid duplicate analytics events. See global.js fetch wrapper around line 2420.

    // Announce the completed add so listeners can react (e.g. the quick-add modal
    // self-closes + attributes the add to Stylitics). Additive: no existing caller
    // listens for this, so it is safe for everyone already using alpAddToCart().
    document.dispatchEvent(new CustomEvent('cart:added', { detail: { item: item, cart: data } }));

    // Open the cart drawer by clicking the header bubble (same trigger the blocks
    // use). Pass { openDrawer: false } to add silently.
    if (opts.openDrawer !== false) {
      var bubble = document.getElementById('cart-icon-bubble');
      if (bubble) bubble.click();
      pushViewedSideCartDL(); // side cart is now open
    }

    return data;
  }

  window.alpAddToCart = alpAddToCart;

  /**
   * alpAddBundle — add several lines as one bundle, all-or-nothing.
   *
   * Adds each { id, quantity, properties } via /cart/add.js with 60ms spacing
   * (the same cadence the charm bundle uses in cart-bundle.js), collecting the
   * returned line keys. If ANY add fails, every already-added key is rolled back
   * via cart_update_url (qty 0) and the error is re-thrown — the cart never ends
   * up holding a half-built bundle. On success, refreshes the mini-cart and opens
   * the drawer (same tail as alpAddToCart). Pass { openDrawer: false } to stay closed.
   *
   * Returns the array of added line keys.
   */
  async function alpAddBundle(items, opts) {
    opts = opts || {};
    var addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
    var updateUrl = (window.routes && window.routes.cart_update_url) || '/cart/update';
    var addedKeys = [];

    try {
      for (var i = 0; i < items.length; i++) {
        await new Promise(function (r) { setTimeout(r, 60); });
        var res = await fetch(addUrl + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(items[i]),
        });
        if (!res.ok) throw new Error('cart/add ' + res.status);
        var data = await res.json();
        if (data && data.key) addedKeys.push(data.key);
        // Analytics: global.js already monitors fetch calls to /cart/add.js via its
        // fetch wrapper, so duplicate pushes are avoided. See global.js line 2420+.
      }
    } catch (e) {
      if (addedKeys.length) {
        var updates = {};
        addedKeys.forEach(function (k) { updates[k] = 0; });
        await fetch(updateUrl + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: updates }),
        }).catch(function () {});
      }
      throw e;
    }

    if (window.updateminicart) await window.updateminicart();
    else if (window.updatecart) await window.updatecart();

    if (opts.openDrawer !== false) {
      var bubble = document.getElementById('cart-icon-bubble');
      if (bubble) bubble.click();
      pushViewedSideCartDL(); // side cart is now open
    }

    return addedKeys;
  }

  window.alpAddBundle = alpAddBundle;
})();